import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { createPortal } from 'react-dom';
import "../styles/Products.css";

const ProductModal = ({ product, onClose }) => {
    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape') onClose();
        };
        document.addEventListener('keydown', handleEscape);
        document.body.style.overflow = 'hidden';

        return () => {
            document.removeEventListener('keydown', handleEscape);
            document.body.style.overflow = 'auto';
        };
    }, [onClose]);

    const modalContent = (
        <AnimatePresence>
            <motion.div
                className="modal-overlay"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={onClose}
            >
                <motion.div
                    className="modal-content"
                    initial={{ scale: 0.8, opacity: 0, y: 50 }}
                    animate={{ scale: 1, opacity: 1, y: 0 }}
                    exit={{ scale: 0.8, opacity: 0, y: 50 }}
                    transition={{ type: "spring", damping: 20 }}
                    onClick={(e) => e.stopPropagation()}
                    style={{ borderTop: `5px solid ${product.color}` }}
                >
                    <button className="modal-close" onClick={onClose}>
                        &times;
                    </button>

                    <div className="modal-header">
                        <div className="modal-icon" style={{ color: product.color }}>
                            <span style={{ fontSize: "3rem" }}>{product.icon}</span>
                        </div>
                        <h2 className="modal-title fsh2">{product.title}</h2>
                        <p className="modal-subtitle fsp">{product.description}</p>
                    </div>

                    <div className="modal-body">
                        {/* Real Examples Section - ADD THIS NEW SECTION */}
                        <div className="real-examples-section mb-5">
                            <h3 className="section-heading">
                                <span style={{ color: product.color }}>Real-World Examples</span>
                            </h3>

                            <div className="examples-grid">
                                {product.examples && product.examples.map((example, index) => {
                                    const link =
                                        example.playStoreId
                                            ? `https://play.google.com/store/apps/details?id=${example.playStoreId}`
                                            : example.url;

                                    return (
                                        <div
                                            key={index}
                                            className="example-card clickable"
                                            onClick={() => link && window.open(link, "_blank")}
                                            role="button"
                                            tabIndex={0}
                                            onKeyDown={(e) => {
                                                if (e.key === "Enter") link && window.open(link, "_blank");
                                            }}
                                        >
                                            <div className="example-header">
                                                <h4 className="example-title">{example.name}</h4>
                                                {example.type && (
                                                    <span className="example-type-badge">{example.type}</span>
                                                )}
                                            </div>

                                            <p className="example-description">{example.description}</p>

                                            {example.features && (
                                                <div className="example-features">
                                                    <strong>Key Features:</strong>
                                                    <ul>
                                                        {example.features.map((feature, idx) => (
                                                            <li key={idx}>{feature}</li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>

                        </div>

                        <div className="row">
                            <div className="col-md-6">
                                <div className="feature-section">
                                    <h3 className="section-heading">
                                        <span style={{ color: product.color }}>Our Capabilities</span>
                                    </h3>
                                    <ul className="feature-list">
                                        {product.details.features.map((feature, index) => (
                                            <li key={index}>
                                                <span className="check-icon">✓</span>
                                                {feature}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>

                            <div className="col-md-6">
                                <div className="tech-section">
                                    <h3 className="section-heading">
                                        <span style={{ color: product.color }}>Technologies We Use</span>
                                    </h3>
                                    <div className="tech-tags">
                                        {product.details.technologies.map((tech, index) => (
                                            <span key={index} className="tech-tag">
                                                {tech}
                                            </span>
                                        ))}
                                    </div>
                                </div>

                                <div className="benefits-section mt-4">
                                    <h3 className="section-heading">
                                        <span style={{ color: product.color }}>Business Benefits</span>
                                    </h3>
                                    <div className="benefits-grid">
                                        {product.details.benefits.map((benefit, index) => (
                                            <div key={index} className="benefit-card">
                                                <div
                                                    className="benefit-icon"
                                                    style={{ backgroundColor: `${product.color}20` }}
                                                >
                                                    {index + 1}
                                                </div>
                                                <p>{benefit}</p>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </motion.div>
            </motion.div>
        </AnimatePresence>
    );

    return createPortal(modalContent, document.body);
};

export default ProductModal;